/**
 * 履歴画面 (①②③) の「遷移元に戻る」導線 (Issue #890)。
 *
 * - 遷移元の現場WG画面から別ウィンドウで開かれている場合は「閉じる」ボタンを出す。
 *   遷移元の画面はそのウィンドウに残っているので、閉じれば遷移前の表示状態のまま戻れる。
 *   閉じられなかった (ブラウザが拒否した) ときは、手動で閉じる案内を出す。
 * - 直接開かれた場合 (変更通知メールのリンクなど) は、遷移元のウィンドウが無いので
 *   現場WG画面へ遷移する「遷移元へ戻る」ボタンを出す。戻り先は対象のマスタ種別から決まる。
 *   種別が分からない (① で「すべて」、未知の `entity_key`) か、その画面を見る権限が無い
 *   (`forbidden`) 場合は、戻り先を決められない / 入れないので何も描画しない。
 * - ヘッダーのタブから入った場合 (`hidden`) は、戻る遷移元が無いので「閉じる」も
 *   「遷移元へ戻る」も描画しない。
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import type { MasterHistoryType } from '../api/types';
import { buildSourceScreenPath } from '../pages/masterHistoryView';
import { MASTER_HISTORY_SOURCE_SCREEN_LABEL } from '../utils/masterHistoryLabels';
import { isOpenedFromSourceWindow } from '../utils/masterHistoryWindow';

/** `window.close()` の後、ウィンドウがまだ生きていれば閉じられなかったと判断するまでの待ち時間 */
const CLOSE_CHECK_DELAY_MS = 300;

export const CLOSE_FAILED_MESSAGE =
  'ウィンドウを閉じられませんでした。ブラウザのタブを直接閉じてください';

export interface MasterHistorySourceBackButtonProps {
  masterType: MasterHistoryType | undefined;
  /** 対象のマスタ種別の機能を持たないとき true (戻り先の画面にも入れない) */
  forbidden?: boolean;
  /** ヘッダーのタブから入ったとき true (遷移元が無いので何も描画しない) */
  hidden?: boolean;
  onNavigate: (path: string) => void;
  /** 戻るボタンの testid。閉じるボタンは `<testId>-close` */
  testId: string;
}

export function MasterHistorySourceBackButton(
  props: MasterHistorySourceBackButtonProps,
): React.JSX.Element | null {
  const { masterType, forbidden = false, hidden = false, onNavigate, testId } = props;
  const [closeFailed, setCloseFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  if (hidden) return null;

  if (isOpenedFromSourceWindow()) {
    const handleClose = (): void => {
      window.close();
      // 閉じられたらこの処理自体が止まる。タイマーが発火した = 閉じられなかった。
      timerRef.current = setTimeout(() => setCloseFailed(true), CLOSE_CHECK_DELAY_MS);
    };
    return (
      <>
        <button type="button" onClick={handleClose} data-testid={`${testId}-close`}>
          閉じる
        </button>
        {closeFailed && (
          <span role="alert" data-testid={`${testId}-close-failed`}>
            {CLOSE_FAILED_MESSAGE}
          </span>
        )}
      </>
    );
  }

  if (masterType === undefined || forbidden) return null;
  return (
    <button
      type="button"
      onClick={() => onNavigate(buildSourceScreenPath(masterType))}
      data-testid={testId}
      data-master-type={masterType}
    >
      {MASTER_HISTORY_SOURCE_SCREEN_LABEL[masterType]}画面（遷移元）へ戻る
    </button>
  );
}
